import React, { useState, useContext, useEffect, useRef } from 'react';
import LiteCurrencySelector from './LiteCurrencySelector';
import { SettingsContext } from './AccountSettingsButton';
import jsDig from 'utils/jsDig.js';

export default function Basic() {
  const context = useContext(SettingsContext);

  const [companyName, setCompanyName] = useState(jsDig(["companyName"], context) || '');
  const [baseCurrency, setBaseCurrency] = useState(jsDig(["baseCurrency"], context) || '');
  const [displayCurrency, setDisplayCurrency] = useState(jsDig(["displayCurrency"], context) || '');
  const [defaultObjective, setDefaultObjective] = useState(jsDig(["defaultObjective"], context) || '');
  const [overviewDatasource, setOverviewDatasource] = useState(jsDig(["overviewDatasource"], context) || '');
  const [website, setWebsite] = useState(jsDig(["website"], context) || '');
  const [isPrimarySiteValid, setPrimarySiteValid] = useState(true); 
  

  const defaultLogo = require("images/lite/icons/logo-placeholder.png");
  let uploadIcon = require("images/lite/icons/upload.png");
  uploadIcon = <img src={`/datapulse${uploadIcon}`} />;
  const inputRef = useRef();
  const [clientLogo, setClientLogo] = useState(context.clientLogo ? context.clientLogo : `/datapulse${defaultLogo}`);
  const companyLogo = <img key={clientLogo} style={{ maxWidth: "101px", maxHeight: "101px", width: "auto", height: "auto" }} src={clientLogo} />;


  useEffect(() => {
    setCompanyName(jsDig(["companyName"], context) || '');
    setWebsite(context.website || '');
    setDefaultObjective(context.defaultObjective || '');
    setOverviewDatasource(context.overviewDatasource || '');
    setBaseCurrency(context.baseCurrency || '');
    setDisplayCurrency(context.displayCurrency || '');
  }, [context.website, context.defaultObjective, context.overviewDatasource, context.baseCurrency, context.displayCurrency, context.companyName]);
  

  const handleCompanyNameChange = (e) => {
    setCompanyName(e.target.value);
  };

  const handleCompanyNameBlur = () => {
    context.setCompanyName(companyName);
  };

  const handlePrimarySiteChange = (event) => {
    const url = event.target.value;
    setWebsite(url);
    setPrimarySiteValid(validatePrimarySite(url));
  };

  const handleDefaultObjectiveChange = (event) => {
    const newObjective = event.target.value;
    setDefaultObjective(newObjective); // Update local state
    context.setDefaultObjective(newObjective); // Update context state
  };

  const handleOverviewDatasourceChange = (event) => {
    const newOverviewDatasource = event.target.value;
    setOverviewDatasource(newOverviewDatasource); // Update local state
    context.setOverviewDatasource(newOverviewDatasource); // Update context state
  }

  const handleBaseCurrencyChange = (e) => {
    const newBaseCurrency = e.target.value;
    setBaseCurrency(newBaseCurrency);
    context.setBaseCurrency(newBaseCurrency); // Update context immediately
  };

  const handleDisplayCurrencyChange = (e) => {
    const newDisplayCurrency = e.target.value;
    setDisplayCurrency(newDisplayCurrency);
    context.setDisplayCurrency(newDisplayCurrency); // Update context immediately
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > 10000000) {
        alert("Image must be less than 10MB. Consider using another image or compressing this one.");
    } else if (file) {
        const fileUrl = URL.createObjectURL(file);
        setClientLogo(fileUrl); // Update state with the new logo URL to trigger re-render
        context.setClientLogo(file); // Update context if needed

    }
  };

  const handlePrimarySiteBlur = () => {
    context.setWebsite(website);
  };

  const validatePrimarySite = (url) => {
    const pattern = new RegExp(
      '^https?:\\/\\/' + // protocol (http or https required)
      '(([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '(\\d{1,3}\\.){3}\\d{1,3}' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$', 'i'); // fragment locator
    return pattern.test(url);
  };

  return (
    <div className="lite-basic-container">
      <div className="lite-basic-left subcontainer">
        <div className="company-display-name">
          <p className="input-label">Company display name</p>
          <input
            type="text"
            name="company_name"
            onChange={handleCompanyNameChange}
            onBlur={handleCompanyNameBlur}
            value={companyName}
          />
        </div>
        <div className="company-website">
          <p className="input-label">Company Website</p>
          <input
            type="url"
            name="website"
            value={website}
            onChange={handlePrimarySiteChange}
            onBlur={handlePrimarySiteBlur}
            className={isPrimarySiteValid ? '' : 'invalid-input'}
          />
          {!isPrimarySiteValid && <p className="error-message">Must use valid URL which includes http or https</p>}
        </div>
      </div>
      <div className="lite-basic-middle subcontainer">
        <div className="lite-dashboard-objective">
          <p className="input-label">Default Dashboard Objective</p>
          <div className="select-wrapper">
            <select
              className="dashboard-objective"
              name="dashboard_objective"
              value={defaultObjective}
              onChange={handleDefaultObjectiveChange}
            >
              <option value="sales">Sales</option>
              <option value="conversions">Conversions</option>
            </select>
          </div>
        </div>
        <div className="lite-overview-datasource">
          <p className="input-label">Overview Datasource</p>
          <div className="select-wrapper">
            <select
              className="dashboard-objective"
              name="overview_datasource"
              value={overviewDatasource}
              onChange={handleOverviewDatasourceChange}
            >
              <option value="google_analytics">Google Analytics</option>
              <option value="shopify">Shopify</option>
              <option value="google_sheets">Google Sheets</option>
            </select>
          </div>
        </div>
        <div className="lite-currency">
          <div>
            <p className="currency-type input-label">Base Currency</p>
            <LiteCurrencySelector selectedCurrency={baseCurrency} setSelectedCurrency={handleBaseCurrencyChange} nameAttribute="base_currency" />
          </div>
          <div>
            <p className="currency-type input-label">Display Currency</p>
            <LiteCurrencySelector selectedCurrency={displayCurrency} setSelectedCurrency={handleDisplayCurrencyChange} nameAttribute="display_currency" />
          </div>
        </div>
      </div>
      <div className="lite-basic-right subcontainer">
        <div className="lite-logo">
          <div>
            <p className="input-label">Client Logo</p>
          </div>
          <div>{companyLogo}</div>
          <div>
            <input
              style={{ display: "none" }}
              name="client_logo"
              type="file"
              ref={inputRef}
              onChange={handleLogoChange}
            />
            <button className="button-upload icon-upload" onClick={(e) => {
              e.preventDefault(); 
              inputRef.current.click();
            }}>
              {uploadIcon}
              Upload Logo
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
